import React, { useRef, useState } from "react";
import Select from "react-select";

import {
  CustomDropdownWithChevron,
  CustomMenuWithSearch,
} from "../../../components";
import { customSelectStyles } from "../../../shared/Constants";
import { PitchTypeTag } from "./PitchTypeTag";

interface PitchTypeDropdownProps {
  pitchTypes: string[];
  pitchTypeFilter: string;
  onPitchTypeFilterChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
}

// Pitch type abbreviation to full name mapping
const PITCH_TYPE_MAPPING: Record<string, string> = {
  FF: "Four-Seam Fastball",
  SI: "Sinker",
  CU: "Curveball",
  SL: "Slider",
  CH: "Changeup",
  CT: "Cutter",
  SP: "Splitter",
  KN: "Knuckleball",
  FC: "Fastball-Cutter",
  SC: "Screwball",
  SV: "Slurve",
  CB: "Curveball",
  FB: "Fastball",
};

export const PitchTypeDropdown = React.memo<PitchTypeDropdownProps>(
  ({ pitchTypes, pitchTypeFilter, onPitchTypeFilterChange }) => {
    const [inputValue, setInputValue] = useState("");
    const [menuIsOpen, setMenuIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const allOptions = [
      { value: "all", label: "All Pitches", fullName: "All Pitches" },
      ...pitchTypes.map((type) => ({
        value: type,
        label: type,
        fullName: PITCH_TYPE_MAPPING[type] || type,
      })),
    ];

    // Filter options based on search input
    const filterOption = (option: any, searchInput: string) => {
      if (!searchInput) return true;
      const search = searchInput.toLowerCase();
      return (
        option.data.value.toLowerCase().includes(search) ||
        option.data.fullName.toLowerCase().includes(search)
      );
    };

    const handleMenuClose = () => {
      // Only close if the click was outside the container
      setTimeout(() => {
        if (
          containerRef.current &&
          !containerRef.current.contains(document.activeElement)
        ) {
          setMenuIsOpen(false);
          setInputValue("");
        }
      }, 0);
    };

    return (
      <div
        className="mb-4"
        data-testid="pitch-type-dropdown"
        ref={containerRef}
      >
        <label htmlFor="pitch-type-select" className="sr-only">
          Filter by pitch type
        </label>
        <Select
          inputId="pitch-type-select"
          value={{
            value: pitchTypeFilter,
            label: pitchTypeFilter,
            fullName:
              pitchTypeFilter === "all"
                ? "All Pitches"
                : PITCH_TYPE_MAPPING[pitchTypeFilter] || pitchTypeFilter,
          }}
          onChange={(option) => {
            if (option && option.value) {
              onPitchTypeFilterChange({
                target: { value: option.value },
              } as any);
              setMenuIsOpen(false);
              setInputValue("");
            }
          }}
          options={allOptions}
          filterOption={filterOption}
          inputValue={inputValue}
          onInputChange={(val) => setInputValue(val)}
          onMenuOpen={() => setMenuIsOpen(true)}
          onMenuClose={handleMenuClose}
          menuIsOpen={menuIsOpen}
          formatOptionLabel={(option: any) => {
            if (option.value === "all") {
              return "All Pitches";
            }
            return (
              <div className="flex items-center gap-2">
                <PitchTypeTag pitchType={option.label} />
                <span className="text-gray-700">{option.fullName}</span>
              </div>
            );
          }}
          className="text-sm"
          classNamePrefix="pitch-type"
          isSearchable={false}
          components={{
            DropdownIndicator: CustomDropdownWithChevron,
            IndicatorSeparator: () => null,
            MenuList: CustomMenuWithSearch,
          }}
          styles={{
            ...customSelectStyles,
            option: (base, state) => ({
              ...base,
              backgroundColor: state.isFocused ? "#F3F4F6" : "white",
              color: "#374151",
              cursor: "pointer",
              padding: "8px 12px",
            }),
          }}
          aria-label="Filter movements by pitch type"
        />
      </div>
    );
  },
);

PitchTypeDropdown.displayName = "PitchTypeDropdown";
